import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, DollarSign, TrendingUp, Activity, RefreshCw,
  XCircle, Search,
  UserPlus, BarChart2, ArrowUpCircle,
  ArrowDownCircle, Bot, Zap, X,
  TrendingDown, AlertTriangle, CreditCard,
  Wallet as WalletIcon, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const TABS = [
  { id: 'overview',     label: 'Overview',     icon: <BarChart2 size={15}/> },
  { id: 'users',        label: 'Users',         icon: <Users size={15}/>     },
  { id: 'trades',       label: 'Live Trades',   icon: <Activity size={15}/>  },
  { id: 'withdrawals',  label: 'Withdrawals',   icon: <ArrowUpCircle size={15}/> },
  { id: 'transactions', label: 'Transactions',  icon: <DollarSign size={15}/> },
  { id: 'blocked',      label: 'Blocked',        icon: <AlertTriangle size={15}/> },
];

const PLANS = ['none','starter','pro','elite'];
const PLAN_PRICES = { starter: 29, pro: 59, elite: 99 };

export default function Admin() {
  const [stats, setStats]               = useState(null);
  const [balances, setBalances]         = useState(null);
  const [users, setUsers]               = useState([]);
  const [withdrawals, setWithdrawals]   = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [blocked, setBlocked]           = useState([]);
  const [liveTrades, setLiveTrades]     = useState([]);
  const [liveStats, setLiveStats]       = useState(null);
  const [tab, setTab]                   = useState('overview');
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [wdSearch, setWdSearch]         = useState('');
  const [wdStatus, setWdStatus]         = useState('all');
  const [selectedWd, setSelectedWd]     = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [processing, setProcessing]     = useState(null);

  // Modals!
  const [creditModal, setCreditModal]       = useState(null);
  const [creditAmount, setCreditAmount]     = useState('');
  const [creditNote, setCreditNote]         = useState('');
  const [creditCurrency, setCreditCurrency] = useState('USDT');
  const [refLookup, setRefLookup]           = useState('');
  const [registerModal, setRegisterModal]   = useState(false);
  const [upgradeModal, setUpgradeModal]     = useState(null);
  const [perfModal, setPerfModal]           = useState(null);
  const [perfData, setPerfData]             = useState(null);
  const [perfLoading, setPerfLoading]       = useState(false);
  const [closeModal, setCloseModal]         = useState(false);
  const [credModal, setCredModal]           = useState(null);
  const [credForm, setCredForm]             = useState({ password:'', login:'', server:'' });
  const [credSaving, setCredSaving]         = useState(false);

  const [registerForm, setRegisterForm] = useState({
    full_name: '', email: '', password: '', plan: 'none'
  });
  const [upgradeForm, setUpgradeForm] = useState({
    plan: 'starter', months: 1
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, wdRes, txRes, liveRes, balRes, blkRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/withdrawals'),
        api.get('/admin/transactions'),
        api.get('/admin/all-trades').catch(() => ({ data: { positions: [], total_profit: 0 } })),
        api.get('/admin/balances').catch(() => ({ data: null })),
        api.get('/admin/blocked-deposits').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
      setWithdrawals(wdRes.data || []);
      setTransactions(txRes.data || []);
      setLiveTrades(liveRes.data?.positions || []);
      setLiveStats(liveRes.data);
      setBalances(balRes.data);
      setBlocked(blkRes.data || []);
    } catch (err) {
      toast.error('Failed to load admin data!');
    } finally {
      setLoading(false);
    }
  };

  // When a bank/momo payout has no stored issuer, ask the admin for it.
  // Returns { cancelled } or { issuer } (issuer may be undefined if already set).
  const resolveIssuer = (w) => {
    if (w.method === 'bank') {
      const existing = w.bank_code || (/^\d+$/.test((w.bank_name || '').trim()) ? w.bank_name.trim() : '');
      if (existing) return { issuer: undefined };
      const code = window.prompt(`No bank code on file for ${w.account_name || 'this payout'} (acct ${w.account_number || '-'}).\nEnter the FxKudi bank code:`, '');
      if (code === null) return { cancelled: true };
      return { issuer: code.trim() || undefined };
    }
    const existing = w.network || w.bank_code || w.bank_name || '';
    if (existing) return { issuer: undefined };
    const net = window.prompt('No momo network on file.\nEnter the network (MTN / VODAFONE / AIRTELTIGO):', '');
    if (net === null) return { cancelled: true };
    return { issuer: net.trim() || undefined };
  };

  const handleWithdrawal = async (id, action, extra = {}) => {
    setProcessing(id);
    try {
      const res = await api.post('/admin/withdrawal/action', { withdrawal_id: id, action, ...extra });
      const label = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected'
                  : action === 'retry' ? 'retried' : action === 'complete' ? 'marked paid' : 'marked failed';
      toast.success(res.data?.message || `Withdrawal ${label}!`);
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleRefLookup = async () => {
    const ref = refLookup.trim();
    if (!ref) return;
    try {
      const res = await api.get('/admin/resolve-reference', { params: { reference: ref } });
      setCreditModal(res.data);           // opens the credit modal for this user
      setCreditAmount('');
      setCreditNote(`Bank transfer ${ref}`);
      setCreditCurrency('USD');
      setRefLookup('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reference not found');
    }
  };

  const handleCredit = async (type) => {
    if (!creditAmount || !creditModal) return;
    setProcessing('credit');
    try {
      await api.post(`/admin/${type}`, {
        user_id     : creditModal.id,
        amount      : parseFloat(creditAmount),
        currency    : creditCurrency,
        description : creditNote,
      });
      toast.success(`${type === 'credit' ? 'Credited' : 'Debited'} ${creditCurrency} ${creditAmount}!`);
      setCreditModal(null);
      setCreditAmount('');
      setCreditNote('');
      setCreditCurrency('USDT');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleBlocked = async (txId, action) => {
    if (action === 'release' &&
        !window.confirm('Release these funds and credit the merchant? Only do this after OFAC review clears the sender.')) return;
    setProcessing(`blk-${txId}`);
    try {
      const res = await api.post('/admin/blocked-deposit/action', { tx_id: txId, action });
      toast.success(res.data?.message || 'Done!');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleBotToggle = async (userId, isRunning) => {
    setProcessing(`bot-${userId}`);
    try {
      const action = isRunning ? 'stop' : 'start';
      await api.post(`/admin/bot/${action}/${userId}`);
      toast.success(`Bot ${action}ped!`);
      loadAll();
    } catch (err) {
      toast.error('Bot action failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleAccessToggle = async (userId, field, current) => {
    setProcessing(`access-${userId}-${field}`);
    try {
      await api.post(`/admin/toggle-access/${userId}`, { [field]: !current });
      toast.success(`${field === 'wallet_access' ? 'Wallet' : 'API'} access ${!current ? 'enabled' : 'disabled'}!`);
      loadAll();
    } catch (err) {
      toast.error('Failed to update access!');
    } finally {
      setProcessing(null);
    }
  };

  const handleGenWallet = async (userId) => {
    setProcessing(`wallet-${userId}`);
    try {
      const res = await api.post(`/admin/generate-wallet/${userId}`);
      toast.success(`Wallet generated: ${res.data.okx_address?.slice(0, 12)}...`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate wallet!');
    } finally {
      setProcessing(null);
    }
  };

  const handleCloseAll = async (mode) => {
    setProcessing(`close-${mode}`);
    try {
      const res = await api.post('/admin/trades/close-all', { mode });
      toast.success(`Closed ${res.data.closed} ${mode} trades!`);
      setCloseModal(false);
      loadAll();
    } catch (err) {
      toast.error('Failed to close trades!');
    } finally {
      setProcessing(null);
    }
  };

  const handleRegister = async () => {
    if (!registerForm.email || !registerForm.full_name || !registerForm.password)
      return toast.error('All fields required!');
    setProcessing('register');
    try {
      const res = await api.post('/admin/register-user', registerForm);
      toast.success(`User ${res.data.email} registered!`);
      setRegisterModal(false);
      setRegisterForm({ full_name:'', email:'', password:'', plan:'none' });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleForceReconnect = async (userId) => {
    setProcessing(`reconnect-${userId}`);
    try {
      const res = await api.post(`/admin/users/${userId}/force-reconnect`);
      toast.success(res.data.message || 'Reconnected!');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconnect failed!');
    } finally {
      setProcessing(null);
    }
  };

  const handleCredSave = async () => {
    if (!credModal || !credForm.password) return toast.error('Password is required!');
    setCredSaving(true);
    try {
      await api.patch(`/admin/users/${credModal.id}/trading-credentials`, {
        password : credForm.password,
        login    : credForm.login  || undefined,
        server   : credForm.server || undefined,
      });
      toast.success(`Credentials saved for ${credModal.email}!`);
      setCredModal(null);
      setCredForm({ password:'', login:'', server:'' });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials!');
    } finally {
      setCredSaving(false);
    }
  };

  const handleUpgrade = async () => {
    if (!upgradeModal) return;
    setProcessing('upgrade');
    try {
      await api.post(
        `/admin/upgrade-subscription/${upgradeModal.id}`,
        upgradeForm
      );
      toast.success(`${upgradeModal.email} upgraded to ${upgradeForm.plan}!`);
      setUpgradeModal(null);
      loadAll();
    } catch (err) {
      toast.error('Upgrade failed!');
    } finally {
      setProcessing(null);
    }
  };

  const loadPerformance = async (user) => {
    setPerfModal(user);
    setPerfLoading(true);
    setPerfData(null);
    try {
      const res = await api.get(`/admin/user-performance/${user.id}`);
      setPerfData(res.data);
    } catch (err) {
      toast.error('Failed to load performance!');
    } finally {
      setPerfLoading(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredWithdrawals = withdrawals.filter(w => {
    if (wdStatus !== 'all' && w.status !== wdStatus) return false;
    const q = wdSearch.trim().toLowerCase();
    if (!q) return true;
    return [
      w.user_email, w.user_name, w.account_number, w.account_name,
      w.phone_number, w.bank_name, w.network, w.tx_ref, w.reference, w.ref,
      w.currency, w.method, w.status,
      String(w.amount_local || ''), String(w.amount_usdt || ''),
    ].some(v => v && String(v).toLowerCase().includes(q));
  });

  const pendingIds = filteredWithdrawals.filter(w => w.status === 'pending').map(w => w.id);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every(id => selectedWd.has(id));

  const toggleWd = (id) => setSelectedWd(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllPending = () => setSelectedWd(prev => {
    const n = new Set(prev);
    if (allPendingSelected) pendingIds.forEach(id => n.delete(id));
    else pendingIds.forEach(id => n.add(id));
    return n;
  });
  const handleBulkApprove = async () => {
    const ids = [...selectedWd];
    if (!ids.length) return;
    if (!window.confirm(`Approve ${ids.length} pending payout(s)? Each will be submitted to FxKudi.`)) return;
    setBulkProcessing(true);
    try {
      const res = await api.post('/admin/withdrawal/bulk-approve', { withdrawal_ids: ids });
      toast.success(res.data?.message || 'Bulk approve complete');
      setSelectedWd(new Set());
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Bulk approve failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#00ff88] border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black gradient-text">Admin Panel</h1>
            <p className="text-gray-400 text-sm">Manage everything!</p>
          </div>
          <div className="flex gap-2">
            <motion.button whileHover={{scale:1.05}}
              onClick={() => setRegisterModal(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20 text-xs font-bold">
              <UserPlus size={14}/> New User
            </motion.button>
            <motion.button whileHover={{scale:1.05}} onClick={loadAll}
              className="p-2 rounded-xl glass border border-[#1a1a1a] text-gray-400">
              <RefreshCw size={16}/>
            </motion.button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                tab === t.id
                  ? 'bg-gradient-to-r from-[#00ff88] to-[#00d4ff] text-black'
                  : 'glass text-gray-400 border border-[#1a1a1a] hover:text-white'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label:'Total Users',    value: stats?.total_users || 0,                          icon:<Users size={18}/>,       color:'#00ff88' },
                { label:'Active Subs',    value: stats?.active_subscriptions || 0,                 icon:<CreditCard size={18}/>,  color:'#00d4ff' },
                { label:'Total Revenue',  value: `$${stats?.revenue?.toFixed(0) || 0}`,           icon:<DollarSign size={18}/>,  color:'#ffd700' },
                { label:'Live Trades',    value: liveStats?.total_open || 0,                       icon:<Activity size={18}/>,    color:'#ff6b6b' },
                { label:'Total Deposits', value: `$${stats?.total_deposited?.toFixed(0) || 0}`,    icon:<ArrowDownCircle size={18}/>, color:'#00ff88' },
                { label:'Withdrawals',    value: `$${stats?.total_withdrawn?.toFixed(0) || 0}`,    icon:<ArrowUpCircle size={18}/>,  color:'#ff4444' },
                { label:'Live P&L',       value: `$${liveStats?.total_profit?.toFixed(2) || 0}`,   icon:<TrendingUp size={18}/>,  color: (liveStats?.total_profit || 0) >= 0 ? '#00ff88' : '#ff4444' },
                { label:'Bots Running',   value: users.filter(u => u.bot_running).length,          icon:<Bot size={18}/>,         color:'#a855f7' },
              ].map((s, i) => (
                <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
                  transition={{delay:i*0.05}}
                  className="glass rounded-2xl p-4 border border-[#1a1a1a]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-500 text-xs">{s.label}</span>
                    <span style={{color:s.color}}>{s.icon}</span>
                  </div>
                  <p className="text-xl font-black" style={{color:s.color}}>{s.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Balances across corridors */}
            {(() => {
              const t = balances?.totals || {
                USDT: users.reduce((s,u)=>s+(u.balance_usdt||0),0),
                NGN : users.reduce((s,u)=>s+(u.balance_ngn ||0),0),
                GHS : users.reduce((s,u)=>s+(u.balance_ghs ||0),0),
              };
              const localNgn = balances?.totals_local?.NGN || 0;
              const localGhs = balances?.totals_local?.GHS || 0;
              const cards = [
                { code:'USDT', label:'USDT Held',  color:'#00ff88',
                  value:`$${(t.USDT||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`,
                  note:'available float' },
                { code:'NGN',  label:'NGN Value',  color:'#00d4ff',
                  value:`₦${(t.NGN||0).toLocaleString(undefined,{maximumFractionDigits:0})}`,
                  note:`incl. ₦${localNgn.toLocaleString(undefined,{maximumFractionDigits:0})} local + USDT converted` },
                { code:'GHS',  label:'GHS Value',  color:'#ffd700',
                  value:`₵${(t.GHS||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`,
                  note:`incl. ₵${localGhs.toLocaleString(undefined,{maximumFractionDigits:2})} local + USDT converted` },
              ];
              return (
                <div className="glass rounded-2xl p-5 border border-[#1a1a1a] mb-6">
                  <h3 className="font-bold mb-1 flex items-center gap-2">
                    <WalletIcon size={16} className="text-[#C29500]"/> Total Balances — All Corridors
                  </h3>
                  <p className="text-gray-500 text-xs mb-4">
                    Value held across {balances?.count ?? users.length} accounts. NGN/GHS = local balance + USDT converted at live rate.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {cards.map((b,i) => (
                      <motion.div key={b.code} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:i*0.05}}
                        className="rounded-xl p-4 border border-[#1a1a1a] bg-[#0d0d0d]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-500 text-xs font-semibold">{b.label}</span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg" style={{color:b.color, background:`${b.color}1a`}}>{b.code}</span>
                        </div>
                        <p className="text-2xl font-black" style={{color:b.color}}>{b.value}</p>
                        <p className="text-gray-600 text-[10px] mt-1">{b.note}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Quick Actions */}
            <div className="glass rounded-2xl p-5 border border-[#1a1a1a]">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Zap size={16} className="text-[#00ff88]"/> Quick Actions
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label:'Close Profitable', icon:<TrendingUp size={16}/>,    color:'text-[#00ff88]', bg:'bg-[#00ff88]/10', border:'border-[#00ff88]/20', action:() => handleCloseAll('profitable') },
                  { label:'Close Losing',     icon:<TrendingDown size={16}/>,  color:'text-red-400',   bg:'bg-red-500/10',   border:'border-red-500/20',   action:() => handleCloseAll('losing')     },
                  { label:'Close All Trades', icon:<XCircle size={16}/>,       color:'text-yellow-400',bg:'bg-yellow-500/10',border:'border-yellow-500/20', action:() => setCloseModal(true)          },
                  { label:'Register User',    icon:<UserPlus size={16}/>,      color:'text-[#00d4ff]', bg:'bg-[#00d4ff]/10', border:'border-[#00d4ff]/20', action:() => setRegisterModal(true)       },
                ].map((a, i) => (
                  <motion.button key={i} whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                    onClick={a.action}
                    disabled={!!processing}
                    className={`flex items-center gap-2 p-3 rounded-xl ${a.bg} ${a.color} border ${a.border} text-xs font-bold justify-center transition-all`}>
                    {a.icon}{a.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            {/* Credit by bank-deposit reference */}
            <div className="glass rounded-2xl border border-[#1a1a1a] p-3 mb-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400 font-semibold px-1">Credit by bank reference</span>
              <input value={refLookup} onChange={e => setRefLookup(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRefLookup(); }}
                placeholder="e.g. KUDI-00014"
                className="flex-1 min-w-[160px] bg-[#0d0d0d] border border-[#1a1a1a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C29500]/60"/>
              <button onClick={handleRefLookup}
                className="px-4 py-2 rounded-lg bg-[#C29500] text-black text-xs font-bold">
                Find &amp; Credit
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]/50"/>
            </div>

            <div className="glass rounded-2xl border border-[#1a1a1a] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1a1a1a] bg-[#0d0d0d]">
                      {['User','Plan','Balance','Bot','Access','Actions'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs text-gray-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a1a1a]">
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">No users found.</td></tr>
                    )}
                    {filteredUsers.map((u, i) => (
                      <tr key={i} className="hover:bg-[#1a1a1a]/30 transition-all">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-white text-xs">{u.full_name}</p>
                          <p className="text-gray-500 text-xs">{u.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-lg font-bold ${
                            u.plan === 'elite'   ? 'bg-yellow-500/10 text-yellow-400' :
                            u.plan === 'pro'     ? 'bg-[#C29500]/10 text-[#C29500]' :
                            u.plan === 'starter' ? 'bg-[#00ff88]/10 text-[#00ff88]'  :
                            'bg-gray-500/10 text-gray-400'
                          }`}>
                            {u.plan || 'none'}
                          </span>
                          <p className={`text-xs mt-0.5 ${
                            u.sub_status === 'active' ? 'text-[#00ff88]' : 'text-gray-500'
                          }`}>{u.sub_status || 'inactive'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-[#00ff88] font-bold text-xs">${u.balance_usdt?.toFixed(2) || '0.00'}</p>
                          {(u.balance_ngn > 0 || u.balance_ghs > 0) && (
                            <div className="flex flex-col gap-0.5 mt-0.5">
                              {u.balance_ngn > 0 && <p className="text-gray-400 text-[10px]">₦{u.balance_ngn?.toLocaleString(undefined,{maximumFractionDigits:0})}</p>}
                              {u.balance_ghs > 0 && <p className="text-gray-400 text-[10px]">₵{u.balance_ghs?.toFixed(2)}</p>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <motion.button whileHover={{scale:1.05}}
                            onClick={() => handleBotToggle(u.id, u.bot_running)}
                            disabled={processing === `bot-${u.id}`}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                              u.bot_running
                                ? 'bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20'
                                : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                            }`}>
                            <Bot size={11}/>
                            {processing === `bot-${u.id}` ? '...' : u.bot_running ? 'ON' : 'OFF'}
                          </motion.button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => handleAccessToggle(u.id, 'wallet_access', u.wallet_access)}
                              disabled={processing === `access-${u.id}-wallet_access`}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold border transition-all ${
                                u.wallet_access
                                  ? 'bg-[#00ff88]/10 text-[#00ff88] border-[#00ff88]/20'
                                  : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                              }`}>
                              <WalletIcon size={10}/>
                              {processing === `access-${u.id}-wallet_access` ? '...' : u.wallet_access ? 'Wallet ✓' : 'Wallet ✗'}
                            </motion.button>
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => handleAccessToggle(u.id, 'api_access', u.api_access)}
                              disabled={processing === `access-${u.id}-api_access`}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold border transition-all ${
                                u.api_access
                                  ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20'
                                  : 'bg-gray-500/10 text-gray-500 border-gray-500/20'
                              }`}>
                              <Key size={10}/>
                              {processing === `access-${u.id}-api_access` ? '...' : u.api_access ? 'API ✓' : 'API ✗'}
                            </motion.button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => { setCreditModal(u); setCreditAmount(''); setCreditNote(''); setCreditCurrency('USDT'); }}
                              className="px-2 py-1 rounded-lg bg-[#00ff88]/10 text-[#00ff88] text-xs font-bold border border-[#00ff88]/20">
                              Credit
                            </motion.button>
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => { setUpgradeModal(u); setUpgradeForm({plan: u.plan || 'starter', months: 1}); }}
                              className="px-2 py-1 rounded-lg bg-[#C29500]/10 text-[#C29500] text-xs font-bold border border-[#C29500]/20">
                              Plan
                            </motion.button>
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => loadPerformance(u)}
                              className="px-2 py-1 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] text-xs font-bold border border-[#00d4ff]/20">
                              Stats
                            </motion.button>
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => { setCredModal(u); setCredForm({ password:'', login: u.exness_login || '', server: u.exness_server || '' }); }}
                              className={`px-2 py-1 rounded-lg text-xs font-bold border flex items-center gap-1 ${u.has_password ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                              <Key size={10}/> {u.has_password ? 'Creds ✓' : 'Creds ✗'}
                            </motion.button>
                            {u.has_password && u.exness_login && (
                              <motion.button whileHover={{scale:1.05}}
                                onClick={() => handleForceReconnect(u.id)}
                                disabled={processing === `reconnect-${u.id}`}
                                className="px-2 py-1 rounded-lg bg-orange-500/10 text-orange-400 text-xs font-bold border border-orange-500/20">
                                {processing === `reconnect-${u.id}` ? '...' : 'Reconnect'}
                              </motion.button>
                            )}
                            {!u.has_wallet && (
                              <motion.button whileHover={{scale:1.05}}
                                onClick={() => handleGenWallet(u.id)}
                                disabled={processing === `wallet-${u.id}`}
                                className="px-2 py-1 rounded-lg bg-orange-500/10 text-orange-400 text-xs font-bold border border-orange-500/20">
                                {processing === `wallet-${u.id}` ? '...' : 'Gen Wallet'}
                              </motion.button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Live Trades Tab */}
        {tab === 'trades' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {[
                { label:'Open Trades', value: liveStats?.total_open || 0,                         color:'#00d4ff' },
                { label:'Total P&L',   value: `$${liveStats?.total_profit?.toFixed(2) || '0.00'}`, color: (liveStats?.total_profit||0) >= 0 ? '#00ff88' : '#ff4444' },
                { label:'Accounts',    value: liveStats?.accounts || 0,                            color:'#a855f7' },
              ].map((s, i) => (
                <div key={i} className="glass rounded-xl p-4 border border-[#1a1a1a] text-center">
                  <p className="text-gray-500 text-xs mb-1">{s.label}</p>
                  <p className="font-black text-lg" style={{color:s.color}}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Close Buttons */}
            <div className="flex gap-2 mb-4">
              {[
                { label:'Close Profitable', mode:'profitable', color:'text-[#00ff88] bg-[#00ff88]/10 border-[#00ff88]/20' },
                { label:'Close Losing',     mode:'losing',     color:'text-red-400 bg-red-500/10 border-red-500/20'       },
                { label:'Close All',        mode:'all',        color:'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
              ].map((b, i) => (
                <motion.button key={i} whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                  onClick={() => handleCloseAll(b.mode)}
                  disabled={!!processing}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border transition-all ${b.color}`}>
                  {processing === `close-${b.mode}`
                    ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"/>
                    : <XCircle size={13}/>
                  }
                  {b.label}
                </motion.button>
              ))}
            </div>

            {/* Trades Table */}
            <div className="glass rounded-2xl border border-[#1a1a1a] overflow-hidden">
              {liveTrades.length === 0 ? (
                <div className="text-center py-12">
                  <Activity size={32} className="text-gray-700 mx-auto mb-2"/>
                  <p className="text-gray-500 text-sm">No open trades!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#1a1a1a] bg-[#0d0d0d]">
                        {['User','Symbol','Type','Lots','Open Price','P&L'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs text-gray-500 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1a1a1a]">
                      {liveTrades.map((t, i) => (
                        <tr key={i} className="hover:bg-[#1a1a1a]/30">
                          <td className="px-4 py-3 text-xs text-gray-400">{t.user_email || '-'}</td>
                          <td className="px-4 py-3 text-xs font-bold text-white">{t.symbol}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${
                              t.type === 'POSITION_TYPE_BUY'
                                ? 'bg-[#00ff88]/10 text-[#00ff88]'
                                : 'bg-red-500/10 text-red-400'
                            }`}>
                              {t.type === 'POSITION_TYPE_BUY' ? 'BUY' : 'SELL'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-300">{t.volume}</td>
                          <td className="px-4 py-3 text-xs text-gray-300">{t.openPrice}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-black ${
                              parseFloat(t.profit) >= 0 ? 'text-[#00ff88]' : 'text-red-400'
                            }`}>
                              {parseFloat(t.profit) >= 0 ? '+' : ''}${parseFloat(t.profit || 0).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Withdrawals Tab */}
        {tab === 'withdrawals' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <input value={wdSearch} onChange={e => setWdSearch(e.target.value)}
                placeholder="Search email, name, account, phone, bank, reference, amount…"
                className="flex-1 bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
              <div className="flex gap-2 flex-wrap">
                {['all','pending','processing','completed','failed','rejected'].map(s => (
                  <button key={s} type="button" onClick={() => setWdStatus(s)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold capitalize border transition ${
                      wdStatus === s
                        ? 'bg-[#00ff88] text-black border-[#00ff88]'
                        : 'bg-[#0d0d0d] text-gray-400 border-[#1a1a1a]'
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl border border-[#1a1a1a] overflow-hidden">
              <div className="px-4 py-2 border-b border-[#1a1a1a] flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-gray-500">
                  {filteredWithdrawals.length} of {withdrawals.length} withdrawals
                </span>
                <div className="flex items-center gap-3 flex-wrap">
                  {pendingIds.length > 0 && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                      <input type="checkbox" checked={allPendingSelected} onChange={toggleAllPending}/>
                      Select all pending ({pendingIds.length})
                    </label>
                  )}
                  {selectedWd.size > 0 && (
                    <>
                      <motion.button whileHover={{scale:1.03}} onClick={handleBulkApprove}
                        disabled={bulkProcessing}
                        className="px-3 py-1.5 rounded-lg bg-[#00ff88] text-black text-xs font-bold disabled:opacity-50">
                        {bulkProcessing ? 'Approving…' : `Approve ${selectedWd.size} selected`}
                      </motion.button>
                      <button onClick={() => setSelectedWd(new Set())}
                        className="px-3 py-1.5 rounded-lg bg-[#0d0d0d] text-gray-400 text-xs font-bold border border-[#1a1a1a]">
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1a1a1a] bg-[#0d0d0d]">
                      {['User','Amount','Method','Account','Status','Action'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs text-gray-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a1a1a]">
                    {filteredWithdrawals.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">No matching withdrawals.</td></tr>
                    )}
                    {filteredWithdrawals.map((w, i) => (
                      <tr key={i} className="hover:bg-[#1a1a1a]/30">
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {w.status === 'pending' && (
                              <input type="checkbox" className="mt-0.5"
                                checked={selectedWd.has(w.id)}
                                onChange={() => toggleWd(w.id)}/>
                            )}
                            <div>
                              <p className="text-xs text-gray-300">{w.user_email || '-'}</p>
                              {(w.ref || w.tx_ref || w.reference) && (
                                <p className="text-[10px] text-gray-500 font-mono mt-0.5"
                                   title="Transaction ID">{w.ref || w.tx_ref || w.reference}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-bold text-white">${w.amount_usdt?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{w.currency} {w.amount_local?.toLocaleString()}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 capitalize">{w.method}</td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-300">{w.bank_name || w.network || '-'}</p>
                          <p className="text-xs text-gray-500">{w.account_number || w.phone_number || '-'}</p>
                          <p className="text-xs text-[#00ff88]">{w.account_name || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-lg font-bold ${
                            w.status === 'completed'  ? 'bg-[#00ff88]/10 text-[#00ff88]' :
                            w.status === 'pending'    ? 'bg-yellow-500/10 text-yellow-400' :
                            w.status === 'processing' ? 'bg-[#00d4ff]/10 text-[#00d4ff]' :
                            'bg-red-500/10 text-red-400'
                          }`}>{w.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          {w.status === 'pending' && (
                            <div className="flex gap-1">
                              <motion.button whileHover={processing === w.id ? {} : {scale:1.05}}
                                onClick={() => {
                                  const r = resolveIssuer(w);
                                  if (r.cancelled) return;
                                  handleWithdrawal(w.id, 'approve', r.issuer ? { issuer: r.issuer } : {});
                                }}
                                disabled={processing === w.id}
                                className="px-2 py-1 rounded-lg bg-[#00ff88]/10 text-[#00ff88] text-xs font-bold border border-[#00ff88]/20 disabled:opacity-50 min-w-[32px]">
                                {processing === w.id ? '…' : '✓'}
                              </motion.button>
                              <motion.button whileHover={processing === w.id ? {} : {scale:1.05}}
                                onClick={() => handleWithdrawal(w.id, 'reject')}
                                disabled={processing === w.id}
                                className="px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-xs font-bold border border-red-500/20 disabled:opacity-50 min-w-[32px]">
                                {processing === w.id ? '…' : '✗'}
                              </motion.button>
                            </div>
                          )}
                          {(w.status === 'processing' || w.status === 'completed') && (
                            <div className="flex gap-1">
                              {w.status === 'processing' && (
                                <motion.button whileHover={processing === w.id ? {} : {scale:1.05}}
                                  onClick={() => {
                                    if (window.confirm(`Mark this payout as PAID/completed? Only do this if the ${w.currency} ${(w.amount_local||0).toLocaleString()} payout has actually landed (no money moves; no re-send).`))
                                      handleWithdrawal(w.id, 'complete');
                                  }}
                                  disabled={processing === w.id}
                                  className="px-2 py-1 rounded-lg bg-[#00ff88]/10 text-[#00ff88] text-xs font-bold border border-[#00ff88]/20 disabled:opacity-50 whitespace-nowrap">
                                  {processing === w.id ? '…' : 'Mark Paid'}
                                </motion.button>
                              )}
                              <motion.button whileHover={processing === w.id ? {} : {scale:1.05}}
                                onClick={() => {
                                  if (window.confirm(`Mark this ${w.status} withdrawal as FAILED and refund ${w.currency} ${(w.amount_local||0).toLocaleString()} to the merchant wallet?`))
                                    handleWithdrawal(w.id, 'failed');
                                }}
                                disabled={processing === w.id}
                                className="px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-xs font-bold border border-red-500/20 disabled:opacity-50 whitespace-nowrap">
                                Fail + Refund
                              </motion.button>
                            </div>
                          )}
                          {(w.status === 'failed' || w.status === 'rejected') && (
                            <motion.button whileHover={{scale:1.05}}
                              onClick={() => {
                                const r = resolveIssuer(w);
                                if (r.cancelled) return;
                                if (window.confirm(`Retry this payout? The merchant will be re-debited ${w.currency} ${(w.amount_local||0).toLocaleString()} and the payout re-sent. The debit is kept only if it succeeds.`))
                                  handleWithdrawal(w.id, 'retry', r.issuer ? { issuer: r.issuer } : {});
                              }}
                              disabled={processing === w.id}
                              className="px-2 py-1 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] text-xs font-bold border border-[#00d4ff]/20 whitespace-nowrap">
                              Retry
                            </motion.button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Transactions Tab */}
        {tab === 'transactions' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            <div className="glass rounded-2xl border border-[#1a1a1a] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1a1a1a] bg-[#0d0d0d]">
                      {['User','Type','Amount','Status','Date'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs text-gray-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a1a1a]">
                    {transactions.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500 text-sm">No transactions yet.</td></tr>
                    )}
                    {transactions.map((tx, i) => (
                      <tr key={i} className="hover:bg-[#1a1a1a]/30">
                        <td className="px-4 py-3 text-xs text-gray-300">{tx.user_email || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-lg font-bold capitalize ${
                            tx.type === 'deposit'      ? 'bg-[#00ff88]/10 text-[#00ff88]'  :
                            tx.type === 'withdrawal'   ? 'bg-red-500/10 text-red-400'      :
                            tx.type === 'subscription' ? 'bg-[#C29500]/10 text-[#C29500]':
                            'bg-yellow-500/10 text-yellow-400'
                          }`}>{tx.type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-black ${
                            tx.type === 'deposit' || tx.type === 'referral'
                              ? 'text-[#00ff88]' : 'text-red-400'
                          }`}>
                            {tx.type === 'deposit' || tx.type === 'referral' ? '+' : '-'}
                            ${tx.amount_usdt?.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-lg font-bold ${
                            tx.status === 'completed' ? 'bg-[#00ff88]/10 text-[#00ff88]' :
                            'bg-yellow-500/10 text-yellow-400'
                          }`}>{tx.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'blocked' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0"/>
              <p className="text-xs text-gray-300">
                Deposits held by OFAC SDN screening. Funds are <b>not credited</b> and left
                quarantined on-chain. <b>Release</b> credits the merchant (only after review clears
                the sender); <b>Dismiss</b> keeps the funds frozen and marks it reviewed.
              </p>
            </div>
            <div className="glass rounded-2xl border border-[#1a1a1a] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1a1a1a] bg-[#0d0d0d]">
                      {['Merchant','Amount','Sender','Tx','Date','Action'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs text-gray-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a1a1a]">
                    {blocked.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">No blocked deposits. 🎉</td></tr>
                    )}
                    {blocked.map((b) => (
                      <tr key={b.id} className="hover:bg-[#1a1a1a]/30">
                        <td className="px-4 py-3 text-xs text-gray-300">
                          <div className="font-semibold text-gray-200">{b.user_name}</div>
                          <div className="text-gray-500">{b.user_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-black text-red-400">${b.amount_usdt?.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono break-all max-w-[180px]">{b.sender || '-'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono break-all max-w-[140px]">{b.reference || '-'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {b.created_at ? new Date(b.created_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              disabled={processing === `blk-${b.id}`}
                              onClick={() => handleBlocked(b.id, 'release')}
                              className="text-xs px-3 py-1.5 rounded-lg font-bold bg-[#00ff88]/10 text-[#00ff88] hover:bg-[#00ff88]/20 disabled:opacity-40">
                              Release
                            </button>
                            <button
                              disabled={processing === `blk-${b.id}`}
                              onClick={() => handleBlocked(b.id, 'dismiss')}
                              className="text-xs px-3 py-1.5 rounded-lg font-bold bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-40">
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

      </div>

      {/* Credit/Debit Modal */}
      <AnimatePresence>
        {creditModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              className="glass rounded-2xl p-6 border border-[#1a1a1a] w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Credit / Debit - {creditModal.email}</h3>
                <button onClick={() => setCreditModal(null)}><X size={18} className="text-gray-400"/></button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mb-4">
                {[
                  ['USDT', `$${(creditModal.balance_usdt || 0).toFixed(2)}`],
                  ['USD',  `$${(creditModal.balance_usd || 0).toFixed(2)}`],
                  ['NGN',  `₦${(creditModal.balance_ngn || 0).toLocaleString(undefined,{maximumFractionDigits:0})}`],
                  ['GHS',  `₵${(creditModal.balance_ghs || 0).toFixed(2)}`],
                  ['EUR',  `€${(creditModal.balance_eur || 0).toFixed(2)}`],
                  ['GBP',  `£${(creditModal.balance_gbp || 0).toFixed(2)}`],
                ].map(([c, v]) => (
                  <div key={c} className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-2 py-2 text-center">
                    <p className="text-gray-500 text-[10px] font-semibold">{c}</p>
                    <p className="text-white text-xs font-bold mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-1.5">
                  {['USDT','USD','NGN','GHS','EUR','GBP'].map(c => (
                    <button key={c} type="button" onClick={() => setCreditCurrency(c)}
                      className={`py-2 rounded-xl text-sm font-bold border transition ${
                        creditCurrency === c
                          ? 'bg-[#C29500] text-black border-[#C29500]'
                          : 'bg-[#0d0d0d] text-gray-300 border-[#1a1a1a]'
                      }`}>
                      {c}
                    </button>
                  ))}
                </div>
                <input type="number" placeholder={`Amount (${creditCurrency})`}
                  value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
                <input type="text" placeholder="Note (optional)"
                  value={creditNote} onChange={e => setCreditNote(e.target.value)}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
                <div className="flex gap-2">
                  <motion.button whileHover={{scale:1.02}} onClick={() => handleCredit('credit')}
                    disabled={processing === 'credit'}
                    className="flex-1 py-3 rounded-xl bg-[#00ff88] text-black font-bold text-sm">
                    Credit
                  </motion.button>
                  <motion.button whileHover={{scale:1.02}} onClick={() => handleCredit('debit')}
                    disabled={processing === 'credit'}
                    className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm">
                    Debit
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Register User Modal */}
      <AnimatePresence>
        {registerModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              className="glass rounded-2xl p-6 border border-[#1a1a1a] w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold flex items-center gap-2">
                  <UserPlus size={16} className="text-[#00ff88]"/> Register New User
                </h3>
                <button onClick={() => setRegisterModal(false)}><X size={18} className="text-gray-400"/></button>
              </div>
              <div className="space-y-3">
                <input type="text" placeholder="Full Name"
                  value={registerForm.full_name}
                  onChange={e => setRegisterForm({...registerForm, full_name: e.target.value})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
                <input type="email" placeholder="Email"
                  value={registerForm.email}
                  onChange={e => setRegisterForm({...registerForm, email: e.target.value})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
                <input type="password" placeholder="Password"
                  value={registerForm.password}
                  onChange={e => setRegisterForm({...registerForm, password: e.target.value})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50"/>
                <select value={registerForm.plan}
                  onChange={e => setRegisterForm({...registerForm, plan: e.target.value})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50">
                  {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
                <motion.button whileHover={{scale:1.02}} onClick={handleRegister}
                  disabled={processing === 'register'}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#00ff88] to-[#00d4ff] text-black font-bold text-sm flex items-center justify-center gap-2">
                  {processing === 'register'
                    ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"/>
                    : <><UserPlus size={16}/> Register User</>}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Subscription Modal */}
      <AnimatePresence>
        {upgradeModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              className="glass rounded-2xl p-6 border border-[#1a1a1a] w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold flex items-center gap-2">
                  <CreditCard size={16} className="text-[#C29500]"/> Upgrade Plan
                </h3>
                <button onClick={() => setUpgradeModal(null)}><X size={18} className="text-gray-400"/></button>
              </div>
              <p className="text-gray-400 text-xs mb-4">{upgradeModal.email}</p>
              <div className="space-y-3">
                <select value={upgradeForm.plan}
                  onChange={e => setUpgradeForm({...upgradeForm, plan: e.target.value})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50">
                  {['starter','pro','elite'].map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} - ${PLAN_PRICES[p]}/mo</option>
                  ))}
                </select>
                <select value={upgradeForm.months}
                  onChange={e => setUpgradeForm({...upgradeForm, months: parseInt(e.target.value)})}
                  className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00ff88]/50">
                  {[1,2,3,6,12].map(m => (
                    <option key={m} value={m}>{m} Month{m > 1 ? 's' : ''}</option>
                  ))}
                </select>
                <div className="p-3 bg-[#0d0d0d] rounded-xl border border-[#1a1a1a]">
                  <p className="text-xs text-gray-500">Total Cost</p>
                  <p className="text-lg font-black text-[#00ff88]">
                    ${(PLAN_PRICES[upgradeForm.plan] * upgradeForm.months).toFixed(0)} USDT
                  </p>
                </div>
                <motion.button whileHover={{scale:1.02}} onClick={handleUpgrade}
                  disabled={processing === 'upgrade'}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#C29500] to-[#00d4ff] text-white font-bold text-sm flex items-center justify-center gap-2">
                  {processing === 'upgrade'
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                    : <><CreditCard size={16}/> Upgrade Plan</>}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Performance Modal */}
      <AnimatePresence>
        {perfModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              className="glass rounded-2xl p-6 border border-[#1a1a1a] w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold flex items-center gap-2">
                  <BarChart2 size={16} className="text-[#00d4ff]"/> Trading Performance
                </h3>
                <button onClick={() => setPerfModal(null)}><X size={18} className="text-gray-400"/></button>
              </div>
              <p className="text-gray-400 text-xs mb-4">{perfModal.email}</p>

              {perfLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#00ff88] border-t-transparent rounded-full animate-spin"/>
                </div>
              ) : perfData?.error ? (
                <p className="text-red-400 text-sm text-center py-4">{perfData.error}</p>
              ) : perfData ? (
                <div className="space-y-3">
                  {/* Account Info */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label:'Balance',  value:`$${perfData.account_info?.balance?.toFixed(2) || '0'}`,  color:'#00ff88' },
                      { label:'Equity',   value:`$${perfData.account_info?.equity?.toFixed(2) || '0'}`,   color:'#00d4ff' },
                      { label:'Win Rate', value:`${perfData.win_rate || 0}%`,                              color:'#ffd700' },
                      { label:'Total P&L',value:`$${perfData.total_profit?.toFixed(2) || '0'}`,           color: perfData.total_profit >= 0 ? '#00ff88' : '#ff4444' },
                    ].map((s, i) => (
                      <div key={i} className="bg-[#0d0d0d] rounded-xl p-3 border border-[#1a1a1a]">
                        <p className="text-gray-500 text-xs">{s.label}</p>
                        <p className="font-black text-sm" style={{color:s.color}}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label:'Total Trades', value: perfData.total_trades || 0,   color:'#fff'     },
                      { label:'Winning',      value: perfData.winning_trades || 0,  color:'#00ff88'  },
                      { label:'Losing',       value: perfData.losing_trades || 0,   color:'#ff4444'  },
                    ].map((s, i) => (
                      <div key={i} className="bg-[#0d0d0d] rounded-xl p-3 border border-[#1a1a1a] text-center">
                        <p className="text-gray-500 text-xs">{s.label}</p>
                        <p className="font-black text-lg" style={{color:s.color}}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                  {/* Open Positions */}
                  {perfData.open_positions?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Open Positions ({perfData.open_positions.length})</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {perfData.open_positions.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-[#0d0d0d] rounded-lg border border-[#1a1a1a]">
                            <span className="text-xs font-bold text-white">{p.symbol}</span>
                            <span className={`text-xs font-bold ${parseFloat(p.profit) >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                              ${parseFloat(p.profit || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Close All Confirmation Modal */}
      <AnimatePresence>
        {closeModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}}
              className="glass rounded-2xl p-6 border border-red-500/20 w-full max-w-sm text-center">
              <AlertTriangle size={40} className="text-yellow-400 mx-auto mb-3"/>
              <h3 className="font-bold text-lg mb-2">Close All Trades?</h3>
              <p className="text-gray-400 text-sm mb-6">This will close ALL open trades for ALL users!</p>
              <div className="flex gap-3">
                <button onClick={() => setCloseModal(false)}
                  className="flex-1 py-3 rounded-xl glass border border-[#1a1a1a] text-gray-400 font-bold text-sm">
                  Cancel
                </button>
                <motion.button whileHover={{scale:1.02}}
                  onClick={() => handleCloseAll('all')}
                  disabled={processing === 'close-all'}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm">
                  Close All!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Credentials Modal ── */}
      <AnimatePresence>
        {credModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.9,opacity:0}}
              className="glass rounded-2xl p-6 border border-[#1a1a1a] w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold flex items-center gap-2">
                  <Key size={16} className="text-yellow-400"/>
                  MT5 Credentials
                </h3>
                <button onClick={() => setCredModal(null)}><X size={18} className="text-gray-400"/></button>
              </div>
              <p className="text-gray-400 text-xs mb-4">{credModal.email}</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Login (optional — leave blank to keep current)</label>
                  <input type="text" placeholder={credModal.exness_login || 'MT5 login number'}
                    value={credForm.login}
                    onChange={e => setCredForm({...credForm, login: e.target.value})}
                    className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-yellow-400/50"/>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Server (optional)</label>
                  <input type="text" placeholder={credModal.exness_server || 'e.g. Exness-MT5Real10'}
                    value={credForm.server}
                    onChange={e => setCredForm({...credForm, server: e.target.value})}
                    className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-yellow-400/50"/>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-semibold">MT5 Password *</label>
                  <input type="password" placeholder="Enter MT5 password"
                    value={credForm.password}
                    onChange={e => setCredForm({...credForm, password: e.target.value})}
                    className="w-full bg-[#0d0d0d] border border-yellow-400/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-yellow-400/60"/>
                </div>
                <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}}
                  onClick={handleCredSave} disabled={credSaving || !credForm.password}
                  className="w-full py-3 rounded-xl bg-yellow-500 text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {credSaving
                    ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"/>
                    : <><Key size={14}/> Save Credentials</>}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </Layout>
  );
}
